import React, { useState } from "react";
import Swal from "sweetalert2";

const API_URL =
  process.env.REACT_APP_API_URL || "http://localhost:5001/api";

const OTPModal = ({ show, onClose, onVerified, email }) => {
  const [otp, setOtp] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (!show) return null;

  const handleVerify = async () => {
    if (!/^\d{6}$/.test(otp)) {
      setError("Enter a valid 6-digit OTP.");
      return;
    }

    setError("");
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/registration/verify-otp`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email,
          otp,
        }),
      });

      const data = await response.json();

      if (data.success) {
        await Swal.fire({
          icon: "success",
          title: "OTP Verified Successfully",
          confirmButtonColor: "#0d6efd",
        });
        onVerified();
        onClose();
      } else {
        setError(data.message || "Invalid OTP.");
      }
    } catch (err) {
      console.log(err);
      setError("Verification failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div style={styles.backdrop} onClick={onClose} />

      <div style={styles.modal} role="dialog" aria-modal="true">
        <div style={styles.card}>
          {/* Glow ring */}
          <div style={styles.glowTop} />

          {/* HEADER */}
          <div style={styles.header}>
            <div style={styles.iconWrap}>📧</div>

            <h3 style={styles.title}>Verify Your Email</h3>

            <p style={styles.sub}>
              OTP sent to <span style={styles.email}>{email}</span>
            </p>
          </div>

          {/* OTP */}
          <div style={styles.otpWrap}>
            <input
              type="text"
              inputMode="numeric"
              style={{
                ...styles.otpInput,
                ...(error ? styles.otpInputErr : {}),
              }}
              placeholder="• • • • • •"
              maxLength={6}
              value={otp}
              onChange={(e) => {
                setOtp(e.target.value.replace(/\D/g, "").slice(0, 6));
                setError("");
              }}
              autoFocus
            />

            {error && <p style={styles.error}>⚠ {error}</p>}
          </div>

          {/* FOOTER */}
          <div style={styles.footer}>
            <button type="button" style={styles.cancelBtn} onClick={onClose}>
              Cancel
            </button>

            <button
              type="button"
              style={{
                ...styles.verifyBtn,
                opacity: loading ? 0.75 : 1,
              }}
              onClick={handleVerify}
              disabled={loading}
            >
              {loading ? (
                <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={styles.spinner} />
                  Verifying…
                </span>
              ) : (
                "Verify OTP →"
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Animations */}
      <style>
        {`
          @keyframes popIn {
            0% { opacity: 0; transform: scale(0.9); }
            100% { opacity: 1; transform: scale(1); }
          }
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </>
  );
};

const styles = {
  backdrop: {
    position: "fixed",
    inset: 0,
    zIndex: 1040,
    background: "rgba(15,42,68,0.45)",
    backdropFilter: "blur(6px)",
  },
  modal: {
    position: "fixed",
    inset: 0,
    zIndex: 1050,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "1rem",
  },
  card: {
    position: "relative",
    width: "100%",
    maxWidth: 420,
    background: "#fff",
    border: "1px solid #E4EAF3",
    borderRadius: 20,
    padding: "2rem",
    boxShadow: "0 20px 60px rgba(0,39,76,0.22), 0 0 40px rgba(13,110,253,0.08)",
    overflow: "hidden",
    animation: "popIn 0.3s cubic-bezier(0.34,1.56,0.64,1)",
  },
  glowTop: {
    position: "absolute",
    top: -60,
    left: "50%",
    transform: "translateX(-50%)",
    width: 200,
    height: 120,
    background: "radial-gradient(ellipse, rgba(13,110,253,0.25), transparent 70%)",
    pointerEvents: "none",
  },
  header: { textAlign: "center", marginBottom: "1.5rem" },
  iconWrap: { fontSize: "2rem", marginBottom: 12 },
  title: {
    color: "#00274C",
    fontSize: "1.2rem",
    fontWeight: 800,
    margin: "0 0 6px",
    fontFamily: "'Outfit', sans-serif",
  },
  sub: {
    color: "#6B7C93",
    fontSize: "0.82rem",
    margin: 0,
    fontFamily: "'Outfit', sans-serif",
  },
  email: { color: "#0d6efd", fontWeight: 600 },
  otpWrap: { marginBottom: "1.5rem" },
  otpInput: {
    width: "100%",
    padding: "14px 0 14px 20px",
    textAlign: "center",
    fontSize: "1.6rem",
    letterSpacing: "0.4em",
    fontWeight: 700,
    fontFamily: "'Outfit', sans-serif",
    color: "#0F2A44",
    background: "#F5F8FC",
    border: "1px solid #E4EAF3",
    borderRadius: 12,
    outline: "none",
    transition: "border-color 0.25s, box-shadow 0.25s",
  },
  otpInputErr: {
    borderColor: "#C0392B",
    boxShadow: "0 0 0 3px rgba(192,57,43,0.12)",
  },
  error: {
    color: "#C0392B",
    fontSize: "0.78rem",
    marginTop: 8,
    fontFamily: "'Outfit', sans-serif",
  },
  footer: { display: "flex", gap: 10 },
  cancelBtn: {
    flex: 1,
    padding: "11px 0",
    background: "#F5F8FC",
    border: "1px solid #E4EAF3",
    borderRadius: 10,
    color: "#6B7C93",
    fontSize: "0.88rem",
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: "'Outfit', sans-serif",
    transition: "background 0.2s",
  },
  verifyBtn: {
    flex: 2,
    padding: "11px 0",
    background: "linear-gradient(135deg, #0d6efd, #3d8bfd)",
    border: "none",
    borderRadius: 10,
    color: "#fff",
    fontSize: "0.9rem",
    fontWeight: 700,
    cursor: "pointer",
    fontFamily: "'Outfit', sans-serif",
    boxShadow: "0 4px 16px rgba(13,110,253,0.35)",
    transition: "opacity 0.2s, transform 0.15s",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  spinner: {
    display: "inline-block",
    width: 14,
    height: 14,
    border: "2px solid rgba(255,255,255,0.4)",
    borderTopColor: "#fff",
    borderRadius: "50%",
    animation: "spin 0.7s linear infinite",
  },
};

export default OTPModal;